import React, { useState, useEffect, useRef, useCallback } from 'react';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import Spinner from 'react-bootstrap/Spinner';
import Alert from 'react-bootstrap/Alert';
import Badge from 'react-bootstrap/Badge';
import TestCheckboxList from './TestCheckboxList';
import { useAuth } from '../context/AuthContext';

const UserTestPermissionPanel = ({ show, onHide, user, onPermissionsUpdated }) => {
    const [testsByCategory, setTestsByCategory] = useState([]);
    const [selectedTestIds, setSelectedTestIds] = useState([]);
    const [originalTestIds, setOriginalTestIds] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const { encryptedApiCall, API_BASE_URL } = useAuth();
    const lastFetchedUserId = useRef(null);
    const isFetching = useRef(false);

    // Reset state when modal closes
    useEffect(() => {
        if (!show) {
            setTestsByCategory([]);
            setSelectedTestIds([]);
            setOriginalTestIds([]);
            setError('');
            lastFetchedUserId.current = null;
            isFetching.current = false;
        }
    }, [show]);

    // Memoize fetch function to prevent unnecessary re-creations
    const fetchUserTests = useCallback(async () => {
        // Prevent duplicate fetches
        if (isFetching.current || !user?.id) {
            return;
        }

        isFetching.current = true;
        const userId = user.id;

        try {
            setLoading(true);
            setError('');
            const data = await encryptedApiCall(
                `${API_BASE_URL}/admin/permissions/users/${userId}/tests`,
                { method: 'GET' }
            );
            
            // Only update state if this is still the current user
            // (prevents race conditions if user changes during fetch)
            if (user?.id === userId) {
                setTestsByCategory(data.testsByCategory || []);
                setSelectedTestIds(data.assignedTestIds || []);
                setOriginalTestIds(data.assignedTestIds || []);
                lastFetchedUserId.current = userId;
            }
        } catch (err) {
            // Only set error if this is still the current user
            if (user?.id === userId) {
                setError(err.message || 'Failed to fetch user tests');
            }
        } finally {
            if (user?.id === userId) {
                setLoading(false);
            }
            isFetching.current = false;
        }
    }, [user?.id, encryptedApiCall, API_BASE_URL]);

    // Fetch tests when modal opens or user changes
    useEffect(() => {
        // Only fetch if:
        // 1. Modal is shown
        // 2. User exists
        // 3. User ID is different from last fetched (or never fetched)
        // 4. Not currently fetching
        if (show && user?.id && user.id !== lastFetchedUserId.current && !isFetching.current) {
            fetchUserTests();
        }
    }, [show, user?.id, fetchUserTests]); // Use user.id instead of user object

    const handleTestToggle = (testId, checked) => {
        if (checked) {
            setSelectedTestIds([...selectedTestIds, testId]);
        } else {
            setSelectedTestIds(selectedTestIds.filter(id => id !== testId));
        }
    };

    const handleSelectAll = () => {
        // Flatten: parent -> children -> tests
        const allTestIds = testsByCategory.flatMap(parent => 
            parent.children?.flatMap(child => 
                child.tests?.map(t => t.id) || []
            ) || []
        );
        setSelectedTestIds(allTestIds);
    };

    const handleDeselectAll = () => {
        setSelectedTestIds([]);
    };

    const handleSave = async () => {
        try {
            setSaving(true);
            setError('');

            await encryptedApiCall(
                `${API_BASE_URL}/admin/permissions/users/${user.id}/tests`,
                {
                    method: 'POST'
                },
                {
                    testIds: selectedTestIds
                }
            );

            onPermissionsUpdated();
        } catch (err) {
            setError(err.message || 'Failed to update permissions');
        } finally {
            setSaving(false);
        }
    };

    const hasChanges = () => {
        if (selectedTestIds.length !== originalTestIds.length) return true;
        const sortedSelected = [...selectedTestIds].sort();
        const sortedOriginal = [...originalTestIds].sort();
        return JSON.stringify(sortedSelected) !== JSON.stringify(sortedOriginal);
    };

    const handleClose = () => {
        // State will be reset by useEffect when show becomes false
        onHide();
    };

    // Calculate total tests: iterate through parent -> children -> tests
    const totalTests = testsByCategory.reduce((sum, parent) => {
        if (!parent.children || !Array.isArray(parent.children)) {
            return sum;
        }
        return sum + parent.children.reduce((childSum, child) => {
            const tests = child?.tests || [];
            return childSum + (Array.isArray(tests) ? tests.length : 0);
        }, 0);
    }, 0);
    const selectedCount = selectedTestIds.length;

    return (
        <Modal show={show} onHide={handleClose} size="lg" fullscreen="lg-down">
            <Modal.Header closeButton>
                <Modal.Title>
                    Manage Test Permissions: {user?.username}
                    <Badge bg="secondary" className="ms-2">{user?.role}</Badge>
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {loading ? (
                    <div className="text-center">
                        <Spinner animation="border" />
                        <p className="mt-2">Loading tests...</p>
                    </div>
                ) : (
                    <>
                        {error && <Alert variant="danger">{error}</Alert>}

                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <div>
                                <strong>Selected: {selectedCount} / {totalTests} tests</strong>
                            </div>
                            <div>
                                <Button
                                    variant="outline-primary"
                                    size="sm"
                                    onClick={handleSelectAll}
                                    className="me-2"
                                >
                                    Select All
                                </Button>
                                <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    onClick={handleDeselectAll}
                                >
                                    Deselect All
                                </Button>
                            </div>
                        </div>

                        <TestCheckboxList
                            testsByCategory={testsByCategory}
                            selectedTestIds={selectedTestIds}
                            onTestToggle={handleTestToggle}
                        />
                    </>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose} disabled={saving}>
                    Cancel
                </Button>
                <Button
                    variant="primary"
                    onClick={handleSave}
                    disabled={saving || !hasChanges()}
                >
                    {saving ? 'Saving...' : 'Save Permissions'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default UserTestPermissionPanel;

